import { useQuery } from "@tanstack/react-query";
import { getLocations } from "@coworker/apprestructured/src/task-management/services/locations.service";
import { useMemo } from "react";
import {
  GroupedSalesLocations,
  SalesLocation,
} from "@coworker/apprestructured/src/task-management/types/SalesLocation";
import { ItemNoAndType } from "@coworker/apprestructured/src/task-management/types/TaskDetailTypes";
import {
  isLoading,
  Loading,
} from "@coworker/apprestructured/src/task-management/loading";
import { INTERVAL } from "@coworker/app/src/constants/clientTime";

const matchesSearchQuery = (
  location: SalesLocation,
  query: string
): boolean => {
  if (!query) return true;
  const lowerQuery = query.toLowerCase();
  return (
    location.slid.toLowerCase().includes(lowerQuery) ||
    location.specialityShopName.toLowerCase().includes(lowerQuery)
  );
};

const filterLocations = <T extends SalesLocation>(
  locations: T[],
  searchQuery: string
): T[] => locations.filter((loc) => matchesSearchQuery(loc, searchQuery));

const calculateTotalQuantity = (locations: SalesLocation[]): number =>
  locations.reduce(
    (sum, loc) => sum + (loc.assignedSalesSpaceQuantity || 0),
    0
  );

export const useFilteredSalesLocations = (
  itemNoAndType: ItemNoAndType | Loading | null,
  searchQuery: string
) => {
  const itemNo =
    itemNoAndType && !isLoading(itemNoAndType) ? itemNoAndType.no : null;
  const type =
    itemNoAndType && !isLoading(itemNoAndType) ? itemNoAndType.type : null;

  const {
    data,
    isLoading: isLoadingLocations,
    isError: isErrorLocations,
  } = useQuery({
    queryKey: ["salesLocations", itemNo, type],
    queryFn: () => getLocations(itemNo!, type!),
    enabled: !!itemNo && !!type,
    staleTime: INTERVAL.HOUR,
  });

  // Calculate combined total quantity before filtering
  const totalAssignedSalesSpaceQuantity = useMemo(() => {
    if (!data) return 0;
    const allLocations = [
      ...data.homeAndPrimaryLocations,
      ...data.otherSalesLocations,
    ];
    return calculateTotalQuantity(allLocations);
  }, [data]);

  const filteredLocations = useMemo((): GroupedSalesLocations => {
    if (!data) {
      return {
        homeAndPrimaryLocations: [],
        otherSalesLocations: [],
        sgfLocations: [],
      };
    }

    return {
      homeAndPrimaryLocations: filterLocations(
        data.homeAndPrimaryLocations,
        searchQuery
      ),
      otherSalesLocations: filterLocations(
        data.otherSalesLocations,
        searchQuery
      ),
      sgfLocations: filterLocations(data.sgfLocations, searchQuery),
    };
  }, [data, searchQuery]);

  return {
    filteredLocations,
    isLoadingLocations,
    isErrorLocations,
    totalAssignedSalesSpaceQuantity,
  };
};
